import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { ROUTES } from "@/lib/routes";
import { ArticleCard } from "@/pages/Article";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useT } from "@/i18n";

const COURT_RULINGS = [
  { label: "I SA/Gd 1401/13 — Wyrok WSA w Gdańsku", href: "https://orzeczenia.nsa.gov.pl/doc/0DDC3DD420" },
  { label: "I SA/Łd 436/13 — Wyrok WSA w Łodzi", href: "https://orzeczenia.nsa.gov.pl/doc/455B8ABC91" },
];


export default function Knowledge() {
  const { t, lang } = useT();
  const [articles, setArticles] = useState([]);
  useEffect(() => { api.get("/articles").then((r) => setArticles(r.data.articles.slice(0, 3))).catch(() => {}); }, []);
  const k = t.knowledge;
  const TABS = [
    { id: "czym-jest", label: k.tabs.what },
    { id: "dla-kogo", label: k.tabs.who },
    { id: "bezpieczenstwo", label: k.tabs.safety },
  ];

  return (
    <section id="biuro-wirtualne-wiedza" data-testid="knowledge-section" className="py-16 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400 font-semibold"
        >
          <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-red mr-3 align-middle" />{k.overline}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-4 max-w-2xl"
        >
          {k.heading}
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <Tabs defaultValue="czym-jest" className="mt-10">
            <TabsList data-testid="knowledge-tabs" className="w-full sm:w-auto grid grid-cols-3 sm:inline-flex">
              {TABS.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id} data-testid={`knowledge-tab-${tab.id}`} className="text-xs sm:text-sm">
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="czym-jest" data-testid="knowledge-content-czym-jest">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 lg:p-10">
                <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{k.what.title}</h3>
                <div className="mt-5 space-y-4 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">
                  <p>{k.what.p1}</p>
                  <p>{k.what.p2}</p>
                </div>
                <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400 font-semibold">
                  {k.what.list_title}
                </div>
                <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3.5">
                  {k.what.items.map((b, i) => (
                    <li key={i} className="flex gap-2.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500 mt-2 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="dla-kogo" data-testid="knowledge-content-dla-kogo">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 lg:p-10">
                <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{k.who.title}</h3>
                <p className="mt-5 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">{k.who.intro}</p>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {k.who.blocks.map((b, i) => (
                    <div key={i} data-testid={`knowledge-who-${i + 1}`} className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
                      <div className="font-mono text-xs text-slate-500 dark:text-slate-400 font-semibold">0{i + 1}</div>
                      <h4 className="font-display font-bold text-lg tracking-tight mt-3">{b.title}</h4>
                      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-2">{b.text}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-8 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">{k.who.footer}</p>
              </div>
            </TabsContent>

            <TabsContent value="bezpieczenstwo" data-testid="knowledge-content-bezpieczenstwo">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 lg:p-10">
                <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{k.safety.title}</h3>
                <div className="mt-5 space-y-4 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">
                  <p>
                    <strong className="text-slate-900 dark:text-white">{k.safety.p1_label}</strong> {k.safety.p1}
                  </p>
                  <p>{k.safety.p2}</p>
                  <p>
                    <strong className="text-slate-900 dark:text-white">{k.safety.p3_label}</strong> {k.safety.p3}
                  </p>
                </div>
                <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400 font-semibold">
                  {k.safety.rulings_title}
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  {COURT_RULINGS.map((r) => (
                    <a
                      key={r.href}
                      data-testid={`knowledge-ruling-${r.label.slice(0, 8).replace(/\s|\/+/g, "-").toLowerCase()}`}
                      href={r.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold border border-slate-200 dark:border-slate-700 rounded-full px-4 py-2 hover:border-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors duration-200"
                    >
                      {r.label}
                    </a>
                  ))}
                </div>
                <div className="mt-8 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-xl px-6 py-5 font-display font-bold text-lg tracking-tight w-fit">
                  {k.safety.legal}
                </div>
              </div>
            </TabsContent>

          </Tabs>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-8"
        >
          <Link
            to="/biuro-wirtualne-warszawa/dokumenty"
            data-testid="knowledge-documents-tile"
            className="group relative overflow-hidden bg-brand-navy text-white rounded-2xl p-6 sm:p-8 lg:p-10 flex flex-col md:flex-row md:items-center gap-6 hover:shadow-2xl hover:shadow-brand-navy/30 transition-shadow duration-300"
          >
            <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 w-56 h-56 rounded-full bg-brand-teal/30 blur-3xl" />
            <div aria-hidden="true" className="pointer-events-none absolute right-24 -bottom-20 w-40 h-40 rounded-full bg-brand-yellow/25 blur-3xl" />
            <div className="w-14 h-14 rounded-2xl bg-brand-yellow text-brand-navy flex items-center justify-center shrink-0 group-hover:rotate-6 transition-transform duration-300">
              <FileText size={26} />
            </div>
            <div className="flex-1 relative">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300 font-semibold">{k.docs_tile.overline}</div>
              <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight mt-2">{k.docs_tile.title}</h3>
              <p className="text-sm text-slate-300 leading-relaxed mt-2 max-w-2xl">{k.docs_tile.text}</p>
            </div>
            <span className="relative inline-flex items-center gap-2 bg-white text-brand-navy font-semibold text-sm px-6 py-3 rounded-full w-fit group-hover:bg-brand-teal group-hover:text-white transition-colors duration-200">
              {k.docs_tile.cta} <ArrowUpRight size={16} />
            </span>
          </Link>
        </motion.div>

        {articles.length > 0 && (
          <div data-testid="knowledge-articles" className="mt-16">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">{lang === "pl" ? "Poradniki" : "Guides"}</p>
                <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight mt-2">{lang === "pl" ? "Artykuły o wirtualnym biurze w Warszawie" : "Articles about virtual offices in Warsaw"}</h3>
              </div>
              <Link data-testid="knowledge-articles-all" to={ROUTES.blog} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-teal hover:text-brand-tealDark">{lang === "pl" ? "Wszystkie artykuły" : "All articles"} <ArrowUpRight size={15} /></Link>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
              {articles.map((a) => <ArticleCard key={a.id} a={a} lang={lang} testid={`knowledge-article-${a.slug}`} />)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
